/*global zE*/

/**
 * Class responsible for handling Support Chat via either Zendesk or Crisp.
 */
export default class SupportChatService {
  // Private variables
  _accountsService = null;
  _authService = null;
  _$transitions;
  _transistionCompleted = false;

  // Public variable to store unread messages count.
  unreadMessagesCount = 0;

  /**
   * Constructor for the SupportChatService.
   * @param {Object} accountsService - Service responsible for account-related operations.
   * @param {Object} $transitions - Angular's transition service.
   */
  /** @ngInject **/
  constructor(
    accountsService,
    $transitions,
    authService
  ) {
    this._accountsService = accountsService;
    this._$transitions = $transitions;
    this._authService = authService;
    this.initialize();
  }

  async initialize() {
    this._$transitions.onBefore({}, () => {
      this._transistionCompleted = false;
    });

    // Setting up a path update event handler on successful route transitions.
    this._$transitions.onSuccess({}, (trans) => {
      this._transistionCompleted = true;
    });

  }

  /**
   * Method to ensure that the Angular transition has successfully finished.
   * @returns {Promise} Resolves when the transition is complete.
   */
  async isReady() {
    return new Promise((resolve) => {
      if (this._transistionCompleted) {
        resolve();
      }

      const unsubscribe = this._$transitions.onSuccess({}, () => {
        unsubscribe();
        resolve();
      });
    });
  }

  /**
   * Re-authenticate the user to the chat system.
   */
  async reauthenticate() {
    await this.isReady();
    this._pushCrispUserData();
    this._setupEventHandlers();
  }


  // Public methods to control the chat display.
  async openSupportChat() {
    window.$crisp.push(['do', 'chat:open']);
  }

  async hideSupportChat() {
    window.$crisp.push(['do', 'chat:hide']);
  }

  async showSupportChat() {
    window.$crisp.push(['do', 'chat:show']);
  }

  // Logging out from chat and re-showing the chat widget.
  logoutFromChat() {
    this._logout();
    this.showSupportChat();
  }

  /**
   * Method to show and then open the support chat after a delay.
   * @param {number} delay - Delay in milliseconds.
   */
  async showAndOpenSupportChat(delay = 500) {
    this.showSupportChat();
    await this._sleep(delay);
    this.openSupportChat();
  }

  // Set a handler to manage actions when support chat is closed.
  _setOnSupportChatCloseHandler() {
    // CRISP-specific handler for chat closing event.
    window.$crisp.push(['on', 'chat:closed', async () => {
      await this._sleep(500);
      this.hideSupportChat();
    }]);
  }

  _isAuthenticated() {
    return this._authService.authentication.isAuth;
  }

  getUnreadMessageCount() {
    return $crisp.get('chat:unread:count')
  }

  getIsChatHidden() {
    return $crisp.is('chat:hidden');
  }

  // Monitor and update the unread message count.
  onMessages(callback) {
    window.$crisp.push(['on', 'message:received', () => {
      if (!this.getIsChatHidden()) {
        return;
      }
      
      const count = this.getUnreadMessageCount();
      callback(count);
    }]);
  }

  // Aggregate all event handlers.
  _setupEventHandlers() {
    this._setOnSupportChatCloseHandler();
  }

  // Log out the user from the chat.
  _logout() {
    delete window.CRISP_TOKEN_ID;
    window.$crisp.push(['do', 'session:reset']);
    window.$crisp.push(['off', 'chat:closed']);
    window.$crisp.push(['off', 'message:received']);
  }


  _pushCrispUserData() {
    var userRole = this._accountsService.isBuyer() ? 'buyer' : this._accountsService.isAffiliate() ? 'publisher' : 'admin';
    if (this._accountsService.currentAccount) {
      window.CRISP_TOKEN_ID = this._accountsService.currentAccountUser.token;
      window.$crisp.push(['do', 'session:reset']);

      this.hideSupportChat(); // Need this to prevent reopening.

      window.$crisp.push(['set', 'user:email', this._accountsService.currentUserName]);
      window.$crisp.push(['set', 'session:data', [
        [
          ['role', userRole],
          ['accountId', this._accountsService.currentAccountId],
          ['accountName', this._accountsService.currentAccount.name],
          ['accountMngrId', this._accountsService.currentAccount.accountMngrId ? this._accountsService.currentAccount.accountMngrId : 'None']
        ]
      ]]);
    }
    window.$crisp.push(['set', 'session:segments', [[userRole]]]);
  }

  // A simple sleep function to introduce a delay in execution.
  _sleep(milliseconds) {
    return new Promise((resolve) => {
      setTimeout(resolve, milliseconds);
    });
  }
}